import React, { useEffect, useCallback, useState, useRef } from 'react';

import { Button } from 'components/Button';
import CustomModal from 'components/CustomModal';
import { browserHistory } from 'utils/history';

import { colors } from 'theme/colors';

import { NavigationBlockerType } from './types';

const NavigationBlocker: React.FC<NavigationBlockerType> = ({ when, shouldBlockNavigation, navigateMessage }) => {
  const [showModal, setShowModal] = useState(false);
  const retryRef = useRef<(() => void) | null>(null);
  const unblockRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (when) {
      window.addEventListener('beforeunload', alertUser);
    }
    return () => {
      window.removeEventListener('beforeunload', alertUser);
    };
  }, [when]);

  const alertUser = (event: BeforeUnloadEvent): void => {
    event.preventDefault();
    event.returnValue = '';
  };

  useEffect(() => {
    if (!when) {
      return undefined;
    }

    const unblock = browserHistory.block(({ retry }) => {
      if (shouldBlockNavigation()) {
        retryRef.current = retry;
        setShowModal(true);
      } else {
        unblock();
        unblockRef.current = null;
        retry();
      }
    });

    unblockRef.current = unblock;

    return () => {
      unblock();
      unblockRef.current = null;
    };
  }, [when, shouldBlockNavigation]);

  const closeModal = useCallback((): void => {
    setShowModal(false);
    retryRef.current = null;
  }, []);

  const handleConfirmNavigationClick = useCallback((): void => {
    setShowModal(false);
    const retry = retryRef.current;
    const unblock = unblockRef.current;

    retryRef.current = null;

    // Unblock first, then retry navigation
    if (unblock) {
      unblock();
      unblockRef.current = null;
    }

    if (retry) {
      retry();
    }
  }, []);

  return (
    <CustomModal
      modalWidth="sm"
      openModal={showModal}
      onClose={closeModal}
      modalTitle="Unsaved changes"
      buttonsSpacing="space-between"
      cancelButton={
        <Button id="cancel-action-button" onClick={closeModal} variant="outlined" size="large">
          No, keep editing
        </Button>
      }
      submitButton={
        <Button
          id="add-action-button"
          onClick={handleConfirmNavigationClick}
          variant="contained"
          size="large"
          color="error"
        >
          Yes, discard changes
        </Button>
      }
    >
      <p
        style={{
          color: colors.text.primary,
          fontSize: '16px',
          letterSpacing: '0.15px',
          margin: '0',
        }}
      >
        {navigateMessage}
      </p>
    </CustomModal>
  );
};

export default NavigationBlocker;
